import { useValue } from "@legendapp/state/react";
import type { ThemePreference } from "@repo/shared/types/theme";
import {
  useCommandPalette,
  useRegisterActions,
} from "@repo/ui/lib/actions/action-registry";
import { SHORTCUTS } from "@repo/ui/lib/actions/shortcuts";
import type { SubPageOption } from "@repo/ui/lib/actions/types";
import { useSidebar } from "@repo/ui/ui/sidebar";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useMemo } from "react";

import { boards$ } from "~/stores/boards";
import { datasets$ } from "~/stores/datasets";
import { pickAndUploadLocalArrowDataset } from "~/stores/datasets/local-uploads";
import { datasources$ } from "~/stores/datasources";
import { theme$ } from "~/stores/theme.store";

const THEME_OPTIONS: {
  id: ThemePreference;
  name: string;
  icon: string;
}[] = [
  { id: "light", name: "Light", icon: "sun" },
  { id: "dark", name: "Dark", icon: "moon-star" },
  { id: "system", name: "System", icon: "mask" },
];

const THEME_ICON_MAP: Record<ThemePreference, string> = {
  light: "sun",
  dark: "moon-star",
  system: "mask",
};

/**
 * Registers app-wide actions that are always available.
 * Must be rendered inside both ActionProvider and SidebarProvider.
 */
export const GlobalActions = () => {
  const navigate = useNavigate();
  const { toggleSidebar, open: sidebarOpen } = useSidebar("left");
  const palette = useCommandPalette();
  const themePreference = useValue(theme$.preference);

  const activeBoardId = useRouterState({
    select: (s) => {
      const match = s.location.pathname.match(/^\/boards\/(.+)/);
      return match?.[1] ?? null;
    },
  });

  const activeDatasetId = useRouterState({
    select: (s) => {
      const match = s.location.pathname.match(/^\/datasets\/(.+)/);
      return match?.[1] ?? null;
    },
  });

  const activeDatasourceId = useRouterState({
    select: (s) => {
      const match = s.location.pathname.match(/\/settings\/datasources\/(.+)/);
      return match?.[1] ?? null;
    },
  });

  const isSettingsPage = useRouterState({
    select: (s) => s.location.pathname.startsWith("/settings"),
  });

  const themeOptions: SubPageOption[] = THEME_OPTIONS.map((opt) => ({
    id: `theme-${opt.id}`,
    name: opt.name,
    icon: opt.icon,
    checked: themePreference === opt.id,
    perform: () => theme$.preference.set(opt.id),
  }));

  const boardFingerprint = useValue(() => {
    const boardsMap = boards$.get() ?? {};
    return Object.values(boardsMap)
      .map((b) => `${b.id}:${b.name}:${b.favorite}:${b.order}`)
      .sort()
      .join("|");
  });

  const boardOptions: SubPageOption[] = useMemo(() => {
    const boardsMap = boards$.get() ?? {};
    return Object.values(boardsMap)
      .sort((a, b) => {
        if (a.favorite && !b.favorite) return -1;
        if (!a.favorite && b.favorite) return 1;
        const orderA = a.order ?? "a0";
        const orderB = b.order ?? "a0";
        return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
      })
      .map((board) => ({
        id: `board-${board.id}`,
        name: board.name || "Untitled board",
        icon: "dashboard",
        checked: board.id === activeBoardId,
        perform: () =>
          navigate({ to: "/boards/$boardId", params: { boardId: board.id } }),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFingerprint, activeBoardId, navigate]);

  const datasetFingerprint = useValue(() => {
    const datasetsMap = datasets$.get() ?? {};
    return Object.values(datasetsMap)
      .map((d) => `${d.id}:${d.name}:${d.favorite}:${JSON.stringify(d.avatar)}`)
      .sort()
      .join("|");
  });

  const datasetOptions: SubPageOption[] = useMemo(() => {
    const datasetsMap = datasets$.get() ?? {};
    return [
      // The catalog leads the picker so `g d` also reaches the full list.
      {
        id: "dataset-browse-all",
        name: "Browse catalog",
        icon: "dot-grid",
        keywords: ["all", "catalog", "folders", "library", "organize"],
        perform: () => navigate({ to: "/datasets" }),
      },
      ...Object.values(datasetsMap)
        .sort((a, b) => {
          if (a.favorite && !b.favorite) return -1;
          if (!a.favorite && b.favorite) return 1;
          return (
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
        })
        .map((dataset) => ({
          id: `dataset-${dataset.id}`,
          name: dataset.name || "Untitled dataset",
          icon: "dataset",
          avatar: dataset.avatar,
          checked: dataset.id === activeDatasetId,
          perform: () =>
            navigate({
              to: "/datasets/$datasetId",
              params: { datasetId: dataset.id },
            }),
        })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetFingerprint, activeDatasetId, navigate]);

  const datasourceFingerprint = useValue(() => {
    const datasourcesMap = datasources$.get() ?? {};
    return Object.values(datasourcesMap)
      .map((d) => `${d.id}:${d.name}`)
      .sort()
      .join("|");
  });

  const datasourceOptions: SubPageOption[] = useMemo(() => {
    const datasourcesMap = datasources$.get() ?? {};
    return Object.values(datasourcesMap).map((ds) => ({
      id: `datasource-${ds.id}`,
      name: ds.name || "Untitled datasource",
      icon: "stack-rounded",
      checked: ds.id === activeDatasourceId,
      perform: () =>
        navigate({
          to: "/settings/datasources/$datasourceId",
          params: { datasourceId: ds.id },
        }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasourceFingerprint, activeDatasourceId, navigate]);

  useRegisterActions(
    [
      {
        id: "open-command-palette",
        name: "Command palette",
        group: "General",
        icon: "magnifying-glass",
        shortcut: SHORTCUTS["command-palette"].shortcut,
        perform: () => palette.toggle(),
      },
      {
        id: "toggle-sidebar",
        name: "Toggle sidebar",
        group: "General",
        icon: sidebarOpen ? "pane-left-fill" : "pane-left",
        shortcut: SHORTCUTS["toggle-sidebar"].shortcut,
        // Settings pages keep the sidebar open so the user can always navigate
        // between settings sections. The stored preference is preserved so
        // returning to another page restores the user's choice.
        perform: () => {
          if (isSettingsPage) return;
          toggleSidebar();
        },
      },
      {
        id: "change-theme",
        name: "Change theme...",
        group: "General",
        icon: THEME_ICON_MAP[themePreference],
        shortcut: SHORTCUTS["change-theme"].shortcut,
        perform: () => palette.showPage("change-theme"),
        shortcutPerform: () => {
          const resolved = theme$.theme.get();
          theme$.preference.set(resolved === "dark" ? "light" : "dark");
        },
        subPage: {
          placeholder: "Select theme...",
          icon: THEME_ICON_MAP[themePreference],
          options: themeOptions,
        },
      },
      {
        id: "go-home",
        name: "Go home",
        group: "Navigation",
        icon: "home",
        shortcut: SHORTCUTS["go-home"].shortcut,
        keywords: ["dashboard"],
        perform: () => navigate({ to: "/" }),
      },
      {
        id: "go-boards",
        name: "Go to board...",
        group: "Navigation",
        icon: "dashboard",
        shortcut: SHORTCUTS["go-boards"].shortcut,
        keywords: ["dashboards", "visualizations"],
        perform: () => palette.showPage("go-boards"),
        subPage: {
          placeholder: "Select board...",
          icon: "dashboard",
          options: boardOptions,
        },
      },
      {
        id: "go-datasets",
        name: "Go to dataset...",
        group: "Navigation",
        icon: "dataset",
        shortcut: SHORTCUTS["go-datasets"].shortcut,
        keywords: ["queries", "sql"],
        perform: () => palette.showPage("go-datasets"),
        subPage: {
          placeholder: "Select dataset...",
          icon: "dataset",
          options: datasetOptions,
        },
      },
      {
        id: "browse-datasets",
        name: "Browse catalog",
        group: "Navigation",
        icon: "dot-grid",
        shortcut: SHORTCUTS["browse-datasets"].shortcut,
        keywords: [
          "catalog",
          "all datasets",
          "folders",
          "library",
          "organize",
          "drafts",
        ],
        perform: () => navigate({ to: "/datasets" }),
      },
      {
        id: "go-datasources",
        name: "Go to datasource...",
        group: "Navigation",
        icon: "stack-rounded",
        shortcut: SHORTCUTS["go-datasources"].shortcut,
        keywords: ["connections", "databases", "postgres", "mysql", "bigquery"],
        perform: () => palette.showPage("go-datasources"),
        subPage: {
          placeholder: "Select datasource...",
          icon: "stack-rounded",
          options: datasourceOptions,
        },
      },
      {
        id: "go-knowledge",
        name: "Go to knowledge",
        group: "Navigation",
        icon: "book",
        shortcut: SHORTCUTS["go-knowledge"].shortcut,
        keywords: ["insights", "summaries", "notes", "datasource"],
        perform: () => navigate({ to: "/settings/knowledge" }),
      },
      {
        id: "go-settings",
        name: "Go to settings",
        group: "Navigation",
        icon: "cog",
        shortcut: SHORTCUTS["go-settings"].shortcut,
        keywords: ["preferences", "account", "organization"],
        perform: () => navigate({ to: "/settings" }),
      },
      {
        id: "go-plans",
        name: "Go to plans",
        group: "Navigation",
        icon: "card",
        shortcut: SHORTCUTS["go-plans"].shortcut,
        keywords: ["billing", "subscription", "upgrade", "pricing"],
        perform: () => navigate({ to: "/settings/plans" }),
      },
      {
        id: "go-organization",
        name: "Go to general settings",
        group: "Navigation",
        icon: "building",
        keywords: ["general", "workspace", "org", "organization"],
        perform: () => navigate({ to: "/settings/organization" }),
      },
      {
        id: "go-members",
        name: "Go to members",
        group: "Navigation",
        icon: "person-plus",
        keywords: ["team", "invite", "people"],
        perform: () => navigate({ to: "/settings/members" }),
      },
      {
        id: "go-connections",
        name: "Go to connections",
        group: "Navigation",
        icon: "plug",
        keywords: ["fivetran", "integrations", "sync", "connectors"],
        perform: () => navigate({ to: "/settings/connections" }),
      },
      {
        id: "go-api-keys",
        name: "Go to API & MCP",
        group: "Navigation",
        icon: "command-line",
        keywords: ["mcp", "api", "keys", "agents"],
        perform: () => navigate({ to: "/settings/api-keys" }),
      },
      {
        // Unlisted on purpose: no shortcut and no button anywhere in the UI.
        // Loads an Arrow file from disk as a client-only dataset, for probing
        // how large an OPFS-backed payload the app can handle.
        id: "upload-local-arrow",
        name: "Upload local Arrow file",
        group: "General",
        icon: "dataset",
        keywords: ["opfs", "local", "arrow", "import", "debug"],
        perform: () => {
          pickAndUploadLocalArrowDataset().then((datasetId) => {
            if (!datasetId) return;
            navigate({ to: "/datasets/$datasetId", params: { datasetId } });
          });
        },
      },
      {
        id: "contact-support",
        name: "Chat with us...",
        group: "Help",
        icon: "comment",
        keywords: [
          "support",
          "help",
          "feedback",
          "chat",
          "contact",
          "plain",
          "message",
          "bug",
          "question",
        ],
        perform: () => {
          const plain = (window as unknown as { Plain?: { open?: () => void } })
            .Plain;
          if (plain?.open) {
            plain.open();
          } else {
            console.warn("Plain chat is not loaded yet.");
          }
        },
      },
    ],
    // Only values that change the visible shape of the action list (names, icons, options).
    // Closures (navigate, palette, toggleSidebar) are captured via actionsRef and don't need to trigger re-registration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      themePreference,
      boardFingerprint,
      activeBoardId,
      datasetFingerprint,
      activeDatasetId,
      datasourceFingerprint,
      activeDatasourceId,
      sidebarOpen,
      isSettingsPage,
    ],
  );

  return null;
};
